import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuthUser } from '@/lib/hooks/useAuthUser';
import type { Person } from '@/types';
import { useI18n } from '@/i18n/I18nProvider';
import {
  EnvelopeSimpleIcon,
  PhoneIcon,
  IdentificationCardIcon,
  MapPinIcon,
  CalendarBlankIcon,
  GenderIntersexIcon,
  GlobeIcon,
  WarningCircleIcon,
  ArrowSquareOutIcon,
} from '@/components/icons';
import { useGetMyProfile } from '@/lib/hooks/useGetMyProfile';
import Spinner from '@/components/ui/spinner';
import { StatusCard } from '@/components/StatusCard';
import { formatEmiratesId } from '@/lib/formatters';

export default function ProfilePage() {
  const { t, locale } = useI18n();
  const { emiratesId, phoneNumber: userPhoneNumber, isLoading: authLoading } = useAuthUser();

  const hasEmiratesId = Boolean(emiratesId);

  const { person, error, isLoading } = useGetMyProfile();

  if (authLoading) {
    return <Spinner />;
  }

  const getPrimaryEmail = (p: Person): string => {
    const primaryEmail = p.metadata?.contacts?.find(
      (c: any) => c.isPrimary && c.contactType?.toLowerCase().includes('email')
    );
    const anyEmail = p.metadata?.contacts?.find((c: any) => c.contactType?.toLowerCase().includes('email'));
    return primaryEmail?.value || anyEmail?.value || '';
  };

  const getPrimaryPhone = (p: Person): string => {
    return (userPhoneNumber ?? '').trim();
    // const primaryPhone = p.metadata?.contacts?.find(
    //   (c: any) => c.isPrimary && (c.contactType?.toLowerCase().includes('phone') || c.contactType?.toLowerCase().includes('mobile'))
    // );
    // const anyPhone = p.metadata?.contacts?.find(
    //   (c: any) => c.contactType?.toLowerCase().includes('phone') || c.contactType?.toLowerCase().includes('mobile')
    // );
    // return primaryPhone?.value || anyPhone?.value || '';
  };

  const getDisplayName = (p: Person, lng: string): string => {
    if (lng === 'ar') {
      return [p.givenName, p.familyName].filter(Boolean).join(' ') || p.username || 'User';
    }
    return (
      [p.metadata?.englishFirstName, p.metadata?.englishFamilyName].filter(Boolean).join(' ') || p.username || 'User'
    );
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!hasEmiratesId || error?.title === 'EmiratesId is required') {
    return (
      <StatusCard
        variant="default"
        icon={<WarningCircleIcon size={64} weight="thin" className="mx-auto mb-4 text-amber-500" />}
        message={t.profile.missingEmiratesIdTitle}
        description={t.profile.missingEmiratesIdMessage}
      >
        <a href="https://uaepass.ae" target="_blank" rel="noopener noreferrer" className="aegov-btn">
          <IdentificationCardIcon className="w-5 h-5" />
          <span>{t.profile.updateUAEPass}</span>
          <ArrowSquareOutIcon className="w-4 h-4" />
        </a>
      </StatusCard>
    );
  }

  if (error || !person) {
    return (
      <StatusCard
        variant="error"
        message={locale === 'ar' ? 'فشل تحميل بيانات الملف الشخصي' : 'Failed to load profile data'}
      />
    );
  }

  const displayName = getDisplayName(person, locale);
  const email = getPrimaryEmail(person);
  const phone = getPrimaryPhone(person);
  const arabicName = [person.givenName, person.middleName, person.familyName].filter(Boolean).join(' ');
  const englishName = [
    person.metadata?.englishFirstName,
    person.metadata?.englishSecondName,
    person.metadata?.englishThirdName,
    person.metadata?.englishFourthName,
    person.metadata?.englishFamilyName,
  ]
    .filter(Boolean)
    .join(' ');
  const addresses = person.metadata?.addresses || [];
  const contacts = person.metadata?.contacts || [];

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="flex-1 text-center sm:text-start">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2">{displayName}</h1>
          <div className="flex flex-col sm:flex-row items-center gap-2 text-muted-foreground mb-4">
            <div className="aegov-badge badge-solid">{t.profile.parentAccount}</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          {(email || phone || contacts.length > 0) && (
            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PhoneIcon className="w-5 h-5 text-primary" weight="duotone" />
                  {locale === 'ar' ? 'معلومات الاتصال' : 'Contact Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Primary Email */}
                {email && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors">
                    <EnvelopeSimpleIcon className="w-5 h-5 text-primary mt-0.5" weight="duotone" />
                    <div className="flex-1">
                      <dt className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-2">
                        {locale === 'ar' ? 'البريد الإلكتروني الأساسي' : 'Primary Email'}
                        <Badge variant="outline" className="text-[10px] px-1 py-0">
                          {locale === 'ar' ? 'أساسي' : 'Primary'}
                        </Badge>
                      </dt>
                      <dd className="text-sm text-foreground font-medium">{email}</dd>
                    </div>
                  </div>
                )}

                {/* Primary Phone */}
                {phone && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors">
                    <PhoneIcon className="w-5 h-5 text-primary mt-0.5" weight="duotone" />
                    <div className="flex-1">
                      <dt className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-2">
                        {locale === 'ar' ? 'رقم الهاتف الأساسي' : 'Primary Phone'}
                        <Badge variant="outline" className="text-[10px] px-1 py-0">
                          {locale === 'ar' ? 'أساسي' : 'Primary'}
                        </Badge>
                      </dt>
                      <dd className="text-sm text-foreground font-medium">{phone}</dd>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Personal Details */}
          {(arabicName ||
            englishName ||
            person.metadata?.birthDate ||
            person.metadata?.gender ||
            person.metadata?.nationality ||
            person.metadata?.maritalStatus) && (
            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <IdentificationCardIcon className="w-5 h-5 text-primary" weight="duotone" />
                  {locale === 'ar' ? 'التفاصيل الشخصية' : 'Personal Details'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {arabicName && (
                    <div className="p-3 rounded-lg bg-muted/30">
                      <dt className="text-xs font-medium text-muted-foreground mb-1">
                        {locale === 'ar' ? 'الاسم بالعربي' : 'Arabic Name'}
                      </dt>
                      <dd className="text-sm text-foreground font-medium" dir="rtl" style={{ textAlign: 'right' }}>
                        {arabicName}
                      </dd>
                    </div>
                  )}
                  {englishName && (
                    <div className="p-3 rounded-lg bg-muted/30">
                      <dt className="text-xs font-medium text-muted-foreground mb-1">
                        {locale === 'ar' ? 'الاسم بالإنجليزي' : 'English Name'}
                      </dt>
                      <dd className="text-sm text-foreground font-medium" dir="ltr">
                        {englishName}
                      </dd>
                    </div>
                  )}
                  {person.metadata?.birthDate && (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                      <CalendarBlankIcon className="w-5 h-5 text-muted-foreground mt-0.5" weight="duotone" />
                      <div className="flex-1">
                        <dt className="text-xs font-medium text-muted-foreground mb-1">
                          {locale === 'ar' ? 'تاريخ الميلاد' : 'Birth Date'}
                        </dt>
                        <dd className="text-sm text-foreground font-medium">{person.metadata.birthDate}</dd>
                      </div>
                    </div>
                  )}
                  {person.metadata?.gender && (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                      <GenderIntersexIcon className="w-5 h-5 text-muted-foreground mt-0.5" weight="duotone" />
                      <div className="flex-1">
                        <dt className="text-xs font-medium text-muted-foreground mb-1">
                          {locale === 'ar' ? 'الجنس' : 'Gender'}
                        </dt>
                        <dd className="text-sm text-foreground font-medium capitalize">{person.metadata.gender}</dd>
                      </div>
                    </div>
                  )}
                  {person.metadata?.nationality && (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                      <GlobeIcon className="w-5 h-5 text-muted-foreground mt-0.5" weight="duotone" />
                      <div className="flex-1">
                        <dt className="text-xs font-medium text-muted-foreground mb-1">
                          {locale === 'ar' ? 'الجنسية' : 'Nationality'}
                        </dt>
                        <dd className="text-sm text-foreground font-medium">
                          {locale === 'ar'
                            ? person.metadata.nationalityArabic || person.metadata.nationality
                            : person.metadata.nationality}
                        </dd>
                      </div>
                    </div>
                  )}
                  {person.metadata?.maritalStatus && (
                    <div className="p-3 rounded-lg bg-muted/30">
                      <dt className="text-xs font-medium text-muted-foreground mb-1">
                        {locale === 'ar' ? 'الحالة الاجتماعية' : 'Marital Status'}
                      </dt>
                      <dd className="text-sm text-foreground font-medium capitalize">
                        {person.metadata.maritalStatus}
                      </dd>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Addresses */}
          {addresses.length > 0 && (
            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPinIcon className="w-5 h-5 text-primary" weight="duotone" />
                  {locale === 'ar' ? 'العنوان' : 'Address'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  // Find primary address or fall back to first address
                  const primaryAddress = addresses.find((a: any) => a.isPrimary) || addresses[0];
                  const addressLines = [
                    primaryAddress.addressLine1,
                    primaryAddress.addressLine2,
                    primaryAddress.addressLine3,
                  ].filter(Boolean);
                  const cityCountry = [primaryAddress.city, primaryAddress.state, primaryAddress.country]
                    .filter(Boolean)
                    .join(', ');

                  return (
                    <div className="p-4 rounded-lg border bg-primary/5 border-primary/20 hover:bg-primary/10 transition-all">
                      {/* Header with Badge */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <MapPinIcon className="w-4 h-4 text-primary" weight="duotone" />
                          <span className="text-xs font-semibold text-foreground">
                            {locale === 'ar' ? 'العنوان الأساسي' : 'Primary Address'}
                          </span>
                        </div>
                        <Badge variant="default" className="text-[10px] px-2 py-0.5">
                          {locale === 'ar' ? 'أساسي' : 'Primary'}
                        </Badge>
                      </div>

                      {/* Address Details */}
                      <div className="space-y-2">
                        {addressLines.length > 0 && (
                          <p className="text-sm text-foreground leading-relaxed">{addressLines.join(', ')}</p>
                        )}

                        {cityCountry && <p className="text-sm text-muted-foreground">{cityCountry}</p>}

                        {/* Additional Details */}
                        {(primaryAddress.zipCode || primaryAddress.poBox || primaryAddress.region) && (
                          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-border/50">
                            {primaryAddress.zipCode && (
                              <div className="text-xs">
                                <span className="text-muted-foreground">
                                  {locale === 'ar' ? 'الرمز البريدي:' : 'ZIP:'}{' '}
                                </span>
                                <span className="text-foreground font-medium">{primaryAddress.zipCode}</span>
                              </div>
                            )}
                            {primaryAddress.poBox && (
                              <div className="text-xs">
                                <span className="text-muted-foreground">
                                  {locale === 'ar' ? 'ص.ب:' : 'P.O. Box:'}{' '}
                                </span>
                                <span className="text-foreground font-medium">{primaryAddress.poBox}</span>
                              </div>
                            )}
                            {primaryAddress.region && (
                              <div className="text-xs">
                                <span className="text-muted-foreground">
                                  {locale === 'ar' ? 'المنطقة:' : 'Region:'}{' '}
                                </span>
                                <span className="text-foreground font-medium">{primaryAddress.region}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Quick Info */}
        <div className="space-y-6">
          {/* Status Card */}
          {(person.status || person.identifier || person.sourcedId) && (
            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-base">{locale === 'ar' ? 'الحالة' : 'Status'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <dt className="text-xs font-medium text-muted-foreground mb-2">
                    {locale === 'ar' ? 'حالة الحساب' : 'Account Status'}
                  </dt>
                  <dd>
                    <span className="aegov-badge badge-solid">
                      {person.status === 'active'
                        ? locale === 'ar'
                          ? 'نشط'
                          : 'Active'
                        : locale === 'ar'
                          ? 'غير نشط'
                          : 'Inactive'}
                    </span>
                  </dd>
                </div>
                <Separator />
                <div>
                  <dt className="text-xs font-medium text-muted-foreground mb-2">
                    {locale === 'ar' ? 'الهوية الإماراتية' : 'Emirates ID'}
                  </dt>
                  <dd className="font-mono bg-muted px-2 py-1 rounded">
                    {formatEmiratesId(person.identifier) || '-'}
                  </dd>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
